import { useState } from 'react';

import type { ContactFieldsFragment } from '@/graphql/types';
import { faPlusCircle } from '@fortawesome/free-solid-svg-icons';
import { ContactModal } from '../ContactModal/ContactModal';
import { NotificationsProvider } from '../../../../notifications/Notifications';
import { IconButton } from '../../../../form/Button';
import { EmailRecipientInput } from './EmailRecipientInput';

export interface ReportEmailRecipientsListProps {
    values: string[];
    disabled?: boolean;
    fieldId?: string;
    onValueChange: (index: number, value: string) => void;
    onRowFocus?: (index: number) => void;
    onAddRow: () => void;
}

type PendingContact = {
    email: string;
    index: number;
};

export function ReportEmailRecipientsList({
    values,
    disabled = false,
    fieldId = 'report-email-recipients',
    onValueChange,
    onRowFocus,
    onAddRow,
}: ReportEmailRecipientsListProps) {
    // Tracks which row is currently waiting for a contact to be created.
    const [pendingContact, setPendingContact] = useState<PendingContact | null>(null);

    function handleQuickCreate(email: string, index: number) {
        setPendingContact({ email, index });
    }

    function closeModal() {
        setPendingContact(null);
    }

    function handleContactCreated(contact: ContactFieldsFragment) {
        if (pendingContact) {
            // Updates the specific row that triggered the Quick Create action.
            onValueChange(pendingContact.index, contact.email);
        }

        closeModal();
    }

    function getRowInputName(index: number) {
        return `${fieldId}-row-${index}`;
    }

    return (
        <NotificationsProvider registerGlobalRef={false}>
            <div>
                {/* Uses legacy IDs/classes for Django form layout + jQuery overrides. */}
                <table id="multi-field-table">
                    <tbody>
                        {values.map((value, index) => (
                            <tr className="row" key={`email-recipient-${index}`}>
                                <td>
                                    <EmailRecipientInput
                                        value={value}
                                        onChange={(next) => onValueChange(index, next)}
                                        placeholder={`${gettext('E-mail address')} ${index + 1}`}
                                        disabled={disabled}
                                        name={getRowInputName(index)}
                                        onQuickCreate={handleQuickCreate}
                                        index={index}
                                        onFocus={() => onRowFocus?.(index)}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <IconButton
                    ariaLabel={gettext('ADD')}
                    icon={faPlusCircle}
                    buttonClassName="add-row"
                    disabled={disabled}
                    onClick={onAddRow}
                />
            </div>

            {pendingContact && (
                <ContactModal
                    open
                    onClose={closeModal}
                    onCreateSuccess={handleContactCreated}
                    draftContact={{ email: pendingContact.email }}
                />
            )}
        </NotificationsProvider>
    );
}
